// @flow

/* eslint-disable-next-line */
import { createTheme } from "@mui/material/styles";
import * as styles from "./export.module.scss";

export default createTheme({
    // color palette
    palette: {
        primary: {
            main: styles.primary,
        },
    },

    // typography
    typography: {
        fontFamily: ["Roboto", "Helvetica", "sans-serif"],
        h1: {
            fontSize: "1.5em",
            fontWeight: "500",
            letterSpacing: ".15em",
            textTransform: "uppercase",
        },
        subtitle1: {
            fontSize: ".7em",
            fontWeight: "300",
            letterSpacing: ".15em",
            textTransform: "uppercase",
        },
        body1: {
            fontSize: "1em",
            fontWeight: "400",
            letterSpacing: ".075em",
        },
        caption: {
            fontSize: ".7em",
            fontWeight: "500",
            letterSpacing: ".075em",
            textTransform: "uppercase",
            color: "black",
        },
    },

    // custom styling
    components: {
        // container styling
        MuiContainer: {
            styleOverrides: {
                // application wrapper
                maxWidthXl: {
                    maxWidth: "100%",
                    // height: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignContent: "flex-start",
                    "@media (min-width: 1920px)": {
                        maxWidth: "100%",
                    },
                },

                // screen wrapper
                maxWidthLg: {
                    display: "grid",
                    gap: "2em",
                    placeItems: "center",
                    maxWidth: "100%",
                    flex: "1 1 auto",
                    padding: "4em 2em",
                    "@media (min-width: 1280px)": {
                        maxWidth: "100%",
                    },
                },
            },
        },

        // header
        MuiAppBar: {
            styleOverrides: {
                colorPrimary: {
                    color: styles.white,
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                regular: {
                    padding: "1em 2em",
                },
                dense: {
                    padding: ".75em 2em",
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: styles.primary120,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: styles.white,
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: "2em",
                    color: styles.white,
                },
            },
        },

        // tabs
        MuiTabs: {
            styleOverrides: {
                root: {
                    alignSelf: "flex-end",
                },
                indicator: {
                    display: "none",
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    backgroundColor: styles.secondary,
                    color: styles.primary,
                    fontSize: ".75em",
                    fontWeight: "400",
                    letterSpacing: ".075em",
                    "&.Mui-selected": {
                        backgroundColor: styles.primary,
                        color: styles.white,
                    },
                },
            },
        },
        MuiTabPanel: {
            styleOverrides: {
                root: {
                    alignSelf: "flex-start",
                    padding: 0,
                },
            },
        },

        // table
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    width: "75vw",
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: styles.greyBgDark,
                },
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    backgroundColor: styles.greyBg,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    height: "6em",
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    textAlign: "center",
                },
                head: {
                    fontWeight: "500",
                    letterSpacing: "0.075em",
                    borderBottom: "none",
                },
            },
        },

        // input field
        MuiTextField: {
            styleOverrides: {
                root: {
                    width: "100%",
                    height: "100%",
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    "&.Mui-focused": {
                        color: styles.secondary,
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                filled: {
                    fontSize: ".9em",
                    letterSpacing: "0.075em",
                    textTransform: "uppercase",
                    color: styles.greyDark,
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                underline: {
                    "&:before": {
                        borderBottom: "none",
                    },
                    "&:hover:before": {
                        borderBottom: "none",
                    },
                    "&:after": {
                        borderBottomColor: styles.secondary,
                    },
                },
                multiline: {
                    height: "100%",
                },
            },
        },

        // Dropzone
        MuiDropzoneArea: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: styles.grey,
                    border: "none",
                    padding: theme.spacing(2),
                }),
                text: {
                    color: styles.greyDark,
                    fontSize: ".9em",
                    letterSpacing: "0.075em",
                    marginBottom: 0,
                    marginTop: 0,
                    textAlign: "left",
                    textTransform: "uppercase",
                },
                textContainer: ({ theme }) => ({
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                    gap: theme.spacing(1),
                }),
                icon: {
                    height: "42px",
                    transform: "scale(.6)",
                },
            },
        },
        MuiDropzonePreviewList: {
            styleOverrides: {
                image: {
                    display: "none",
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 0,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: styles.greyDark,
                },
                colorSecondary: {
                    "&.Mui-checked": {
                        color: styles.secondary,
                        "&:hover": {
                            backgroundColor: styles.secondary15,
                        },
                    },
                },
            },
        },

        // button
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: ".75em",
                    fontWeight: "400",
                    letterSpacing: ".075em",
                    borderRadius: "0",
                    padding: ".75em 3em",
                },
                label: {
                    transition:
                        "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                },
                contained: {
                    backgroundColor: styles.primary,
                    "&:hover": {
                        backgroundColor: styles.primary120,
                    },
                },
                containedPrimary: {
                    // maxHeight: "3.25em",
                    backgroundColor: styles.primary,
                    color: styles.white,
                    "&:hover": {
                        backgroundColor: styles.primary15,
                    },
                },
                containedSecondary: {
                    // maxHeight: "3.25em",
                    backgroundColor: styles.secondary,
                    color: styles.primary,
                    "&:hover": {
                        backgroundColor: styles.secondary120,
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: styles.white,
                },
                colorSecondary: {
                    color: styles.secondary,
                    "&:hover": {
                        backgroundColor: styles.secondary15,
                    },
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    fontFamily: ["Roboto", "Helvetica", "sans-serif"],
                    fontSize: ".7em",
                    fontWeight: "500",
                    letterSpacing: ".15em",
                    textTransform: "uppercase",
                },
            },
        },

        // graphics
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontSize: "1em",
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                colorPrimary: {
                    color: styles.primary,
                    backgroundColor: styles.primary40,
                    borderRadius: "50%",
                },
                colorSecondary: {
                    color: styles.secondary,
                    backgroundColor: styles.secondary40,
                    borderRadius: "50%",
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    marginTop: ".5em",
                },
                colorSecondary: {
                    backgroundColor: styles.secondary40,
                },
                barColorSecondary: {
                    backgroundColor: styles.secondary,
                },
            },
        },
    },
});
